import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { fetchJobLogCount, fetchJobLogData } from './api';
import type { JobLogViewerProps } from './types';

/**
 * One job's logs: a dropdown of its runs and the selected run's text.
 *
 * Knows nothing about dialogs, so it can be dropped anywhere and tested with a
 * plain render.
 */
export default function JobLogViewer({ serverId, queueName, jobId }: JobLogViewerProps) {
  const [count, setCount] = useState(0);
  /** Selected log, 1-based. 0 means there is nothing to show. */
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  // The dropdown can outrun the network, so an older response must not
  // overwrite a newer one. ServerQueues guards its poll with the same counter.
  const requestRef = useRef(0);

  const selectId = useId();

  /** Load one log's text. This is what the dropdown calls. */
  const loadData = useCallback(
    async (target: number) => {
      const request = ++requestRef.current;

      setBusy(true);
      setError(null);

      try {
        const data = await fetchJobLogData(serverId, queueName, jobId, target);

        if (!mountedRef.current || request !== requestRef.current) return;

        setText(data);
      } catch (err) {
        if (!mountedRef.current || request !== requestRef.current) return;

        // Keep the last good text on screen, the same way ServerQueues keeps
        // its last good table.
        setError(err instanceof Error ? err.message : 'Failed to get log data.');
      } finally {
        if (mountedRef.current && request === requestRef.current) setBusy(false);
      }
    },
    [serverId, queueName, jobId],
  );

  /**
   * Count the logs, then load one. This is what mount and Reload call.
   *
   * Reload has to re-count because a job that ran again since the dialog opened
   * has a log the dropdown doesn't know about yet. `preferred` keeps the user's
   * selection across a reload when it still exists; pass 0 to take the newest.
   */
  const refresh = useCallback(
    async (preferred: number) => {
      setBusy(true);
      setError(null);

      let target = 0;

      try {
        const total = await fetchJobLogCount(serverId, queueName, jobId);

        if (!mountedRef.current) return;

        setCount(total);

        target = preferred >= 1 && preferred <= total ? preferred : total;

        setIndex(target);

        if (total === 0) setText('');
      } catch (err) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to count logs.');
        }

        return;
      } finally {
        if (mountedRef.current) {
          setLoading(false);

          // Otherwise loadData owns `busy` from here.
          if (target === 0) setBusy(false);
        }
      }

      // Positive check rather than `=== 0`: nothing but a real 1-based index
      // should ever reach the server.
      if (!(target >= 1)) return;

      await loadData(target);
    },
    [serverId, queueName, jobId, loadData],
  );

  // Mount only. `refresh` is stable for a given job and nothing else calls it,
  // so opening the viewer makes exactly one count and one data request. There
  // is deliberately no effect watching `index` - the dropdown fetches directly,
  // which is what keeps this from firing twice on mount.
  useEffect(() => {
    mountedRef.current = true;

    void refresh(0);

    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  /** Newest first - the run you want is almost always the last one. */
  const options = useMemo(
    () => Array.from({ length: count }, (_, position) => count - position),
    [count],
  );

  if (loading) {
    return <div className="spinner centered" />;
  }

  return (
    <div className="job-log-viewer">
      <div className="job-log-viewer__controls">
        <label htmlFor={selectId}>Log</label>

        <select
          id={selectId}
          value={index}
          disabled={busy || count === 0}
          onChange={(event) => {
            const next = Number(event.target.value);

            setIndex(next);
            void loadData(next);
          }}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option === count ? `Run ${option} (latest)` : `Run ${option}`}
            </option>
          ))}
        </select>

        <button type="button" className="small" disabled={busy} onClick={() => void refresh(index)}>
          Reload
        </button>
      </div>

      {error && (
        <div className="error-alert" role="alert">
          {error}
        </div>
      )}

      {count === 0 ? (
        <div className="info-alert">No logs for this job.</div>
      ) : (
        <textarea
          className="job-log-viewer__text"
          value={text}
          readOnly
          // readOnly rather than disabled: the text stays selectable, copyable
          // and focusable, which is the whole point of showing it.
          wrap="off"
          spellCheck={false}
          aria-busy={busy}
          aria-label="Job log"
        />
      )}
    </div>
  );
}
